<script setup lang="ts">
import type { ComponentExposed } from 'vue-component-type-helpers';
import type { DInput, DInputNumber } from '#components';
import type { PrimitiveType } from '#layers/data-model';
import { computed, toValue, useId, useTemplateRef } from 'vue';
import { isNil } from '#layers/base';
import { isPrimitiveTypeBoolean, isPrimitiveTypeDecimal, isPrimitiveTypeJson, isPrimitiveTypeNumber } from '#layers/data-model';
import { useAutoWidthInput } from '#layers/ui';

interface Props {
	primitiveType: PrimitiveType;
	typeOptions?: Record<string, unknown>;
	disabled?: boolean;
	/**
	 * Show a small (short) version of the input
	 */
	small?: boolean;
}

const props = defineProps<Props>();

const value = defineModel<string | number | boolean | Record<string, unknown> | null | undefined>();

const stringValue = computed({
	get() {
		const v = toValue(value);

		if (isNil(v)) {
			return;
		}

		if (isPrimitiveTypeJson(props.primitiveType)) {
			return JSON.stringify(v);
		}

		return String(v);
	},
	set(newValue: string | undefined) {
		if (isPrimitiveTypeJson(props.primitiveType)) {
			try {
				value.value = newValue ? JSON.parse(newValue) : undefined;
			}
			catch {
				return;
			}

			return;
		}

		value.value = newValue === '' ? undefined : newValue;
	},
});

const numberValue = computed({
	get() {
		const v = toValue(value);

		if (isNil(v)) {
			return;
		}

		return Number(v);
	},
	set(newValue: number | undefined) {
		value.value = newValue;
	},
});

const inputRef = useTemplateRef<ComponentExposed<typeof DInput | typeof DInputNumber>>('input');
useAutoWidthInput(() => inputRef.value?.inputRef ?? null);

const inputType = computed(() => {
	if (isPrimitiveTypeDecimal(props.primitiveType)) {
		return 'decimal';
	}

	if (isPrimitiveTypeNumber(props.primitiveType)) {
		return 'number';
	}

	return 'text';
});

const inputId = useId();

const inputUi = computed(() => ({
	base: ['max-w-40 px-2 focus-within:min-w-20', props.small ? 'min-w-5' : 'min-w-20'],
}));

const commonProps = computed(() => ({
	id: inputId,
	variant: 'none',
	color: 'neutral',
	size: 'xs',
	disabled: props.disabled,
	ui: inputUi.value,
} as const));
</script>

<template>
	<DCheckbox
		v-if="isPrimitiveTypeBoolean(primitiveType)"
		:id="inputId"
		v-model="value"
		:disabled
		class="h-full items-center px-1.5"
	/>
	<DInputDecimalNumber
		v-else-if="isPrimitiveTypeDecimal(primitiveType)"
		ref="input"
		v-bind="commonProps"
		v-model="stringValue"
		:scale="primitiveType.params.scale"
		:precision="primitiveType.params.precision"
	/>
	<DInputNumber
		v-else-if="isPrimitiveTypeNumber(primitiveType)"
		ref="input"
		v-bind="commonProps"
		v-model="numberValue"
	/>
	<DInput
		v-else-if="inputType === 'text'"
		ref="input"
		v-bind="commonProps"
		v-model="stringValue"
	/>
</template>
